import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';

/**
 * PwaAutoLogout — In PWA (standalone) mode ONLY, force-logs-out the user
 * whenever the installed app is closed OR left in the background for more
 * than IDLE_THRESHOLD_MS. On the next open, the user always lands on the
 * login page.
 *
 * Two complementary triggers:
 *   1. **sessionStorage marker**: cleared automatically by the OS when the
 *      standalone app is swipe-killed → next open = fresh session = logout.
 *   2. **visibilitychange watchdog**: if the app was hidden more than
 *      IDLE_THRESHOLD_MS, force logout when the user returns.
 *
 * Web-browser users (no standalone mode) are NOT affected.
 */
const IDLE_THRESHOLD_MS = 60 * 1000; // 1 minute in the background = logout
const HIDDEN_AT_KEY = 'pwa_hidden_at';

function detectPwa() {
  if (typeof window === 'undefined') return false;
  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true;
  const stickyPwa = sessionStorage.getItem('pwa_mode') === '1';
  return standalone || stickyPwa;
}

export default function PwaAutoLogout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const inPwaRef = useRef(false);

  const forceLogout = () => {
    try { logout?.(); } catch (_) {}
    try {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      localStorage.removeItem('userId');
      sessionStorage.removeItem('pwa_session_active');
      sessionStorage.removeItem(HIDDEN_AT_KEY);
    } catch (_) {}
    if (!/^\/(login|register|forgot-password|reset-password|$)/.test(window.location.pathname)) {
      navigate('/login', { replace: true });
    }
  };

  // Trigger #1 — fresh open with a stale token in localStorage.
  useEffect(() => {
    inPwaRef.current = detectPwa();
    if (!inPwaRef.current) return;

    const hasToken = !!localStorage.getItem('token');
    const sessionActive = sessionStorage.getItem('pwa_session_active') === '1';
    if (hasToken && !sessionActive) {
      forceLogout();
    }
  }, [location.pathname]);

  // Keep the session marker armed as long as the user has a token.
  useEffect(() => {
    if (!inPwaRef.current) return;
    if (localStorage.getItem('token')) {
      sessionStorage.setItem('pwa_session_active', '1');
    }
  });

  // Trigger #2 — the app was hidden for too long.
  useEffect(() => {
    if (!inPwaRef.current) return;

    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        sessionStorage.setItem(HIDDEN_AT_KEY, String(Date.now()));
      } else if (document.visibilityState === 'visible') {
        const hiddenAt = Number(sessionStorage.getItem(HIDDEN_AT_KEY) || 0);
        if (hiddenAt && Date.now() - hiddenAt > IDLE_THRESHOLD_MS) {
          const hasToken = !!localStorage.getItem('token');
          if (hasToken) forceLogout();
        }
        sessionStorage.removeItem(HIDDEN_AT_KEY);
      }
    };

    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, []);

  return null;
}
